import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query'
import { fetchBaseQuery } from '@reduxjs/toolkit/query'
import { Mutex } from 'async-mutex'

import { setAccessToken, unsetTokens } from './slices/authSlice'
import { resetDataManagementState } from './slices/dataManagementSlice'
import type { AccessTokenPayload } from './types/auth'

import type { RootState } from '.'

const mutex = new Mutex()

export const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const { auth } = getState() as RootState

    if (auth.access_token) {
      headers.set('authorization', `Bearer ${auth.access_token}`)
    }
    return headers
  },
})

async function getCsrfToken(
  api: any,
  extraOptions: any,
): Promise<string | null> {
  const result = await baseQuery(
    { url: '/auth/csrf-token', method: 'GET' },
    api,
    extraOptions,
  )
  if (result.data) {
    return (result.data as { csrfToken: string }).csrfToken
  }
  console.warn('CSRF token fetch failed')
  return null
}

const customFetchBase: BaseQueryFn<
  FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args: FetchArgs, api, extraOptions) => {
  if (args.url.startsWith('/auth') && args.method === 'POST') {
    const token = await getCsrfToken(api, extraOptions)
    if (token) {
      args = {
        ...args,
        headers: {
          ...(args.headers as Record<string, string>),
          'X-CSRF-Token': token,
        },
      }
    }
  }

  await mutex.waitForUnlock()
  let result = await baseQuery(args, api, extraOptions)
  if (
    result.meta?.response?.status === 401 &&
    //do not handle auth endpoints, they don't do token related operations
    !args.url.startsWith('/auth')
  ) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire()
      const state = api.getState() as RootState
      try {
        const refreshResult = await baseQuery(
          {
            url: 'auth/refresh',
            method: 'POST',
            body: {
              refresh_token: state.auth.refresh_token,
            },
          },
          api,
          extraOptions,
        )

        if (refreshResult.data) {
          api.dispatch(setAccessToken(refreshResult.data as AccessTokenPayload))
          result = await baseQuery(args, api, extraOptions)
        } else {
          api.dispatch(unsetTokens())
          api.dispatch(resetDataManagementState())
          // window.location.href = '/login'
        }
      } catch (ex) {
        console.log(ex)
      } finally {
        release()
      }
    } else {
      await mutex.waitForUnlock()
      result = await baseQuery(args, api, extraOptions)
    }
  }

  return result
}

export default customFetchBase
